import { useState, useEffect } from "react"
import VariantCard from "../../elements/variant_card"
import VariantList from "../../elements/variant_list"
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";
import { BASE_URL } from '../../apiConfig';
import Loading from "../../cors/Loading";
import { toast } from 'sonner'; 

export default function VariantsPage() {
  const { token, role, roles } = useAuth();
  const { t } = useLanguage();
  const [variants, setVariants] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("type");
  const [filterType, setFilterType] = useState("all");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVariants = async () => {
    setLoading(true); 
    setError(null); 
    try {
      const response = await fetch(BASE_URL + '/api/variants', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errorText = await response.text(); 
        console.error(`API Error: ${response.status} - ${errorText}`);
        throw new Error(`Failed to fetch variants: ${response.statusText}`);
      }
      const data = await response.json();

      if (Array.isArray(data)) {
        setVariants(data);
      } else if (data && Array.isArray(data.variants)) {
        setVariants(data.variants);
      } else if (data && Array.isArray(data.data)) {
        setVariants(data.data);
      } else {
        setVariants([]);
        toast.error(t('variants.unexpected_format'));
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message || t('variants.error_loading'));
    } finally {
      setLoading(false); // End loading
    }
  }

  useEffect(() => {
    fetchVariants();
  }, []); 

  const filteredVariants = variants
    .filter((variant) => {
      const matchesSearch =
        variant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        variant.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        variant.value?.toLowerCase().includes(searchTerm.toLowerCase());
      // Re-added matchesType as it's a specific filter for variants
      const matchesType = filterType === "all" || variant.type === filterType;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "type":
          return a.type.localeCompare(b.type);
        case "newest":
          return new Date(b.created_at) - new Date(a.created_at);
        case "oldest":
          return new Date(a.created_at) - new Date(a.created_at); // Fixed a typo here
        default:
          return 0;
      }
    });

  const getTypeColor = (type) => {
    switch (type) {
      case "color":
        return "text-purple-600 bg-purple-100";
      case "size":
        return "text-blue-600 bg-blue-100";
      case "material":
        return "text-green-600 bg-green-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const variantTypes = [...new Set(variants.map((v) => v.type))].filter(Boolean); // Ensure no undefined types

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-5 max">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
              {t('variants.list_title')}
            </h1>
            <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
              {t('variants.list_subtitle')}
            </p>
          </div>
          {
            role === 1 || (role === 2 && roles.add_variant === 1) ?
              <div className="mt-6 md:mt-0">
                <Link to="/variants/create">
                  <button className="cursor-pointer text-xs max-xl:py-3 bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white font-semibold max-xl:py-2 max-xl:px-4 py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
                    <span className="flex items-center">
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {t('variants.add_variant')}
                    </span>
                  </button>
                </Link>
              </div>
              :''
          }
        </div>

        {/* Search and Controls */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 px-4 py-2 max-xl:p-4 mb-5">
          <div className="flex flex-col items-center xl:flex-row gap-6 max-xl:gap-2">
            {/* Search Bar */}
            <div className="relative flex-1 py-2 w-full">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder={t('variants.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full dark:bg-stone-900 dark:text-white outline-none text-sm pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)] transition-all duration-200"
              />
            </div>

            {/* Controls */}
            <div className="flex gap-4 max-xl:w-full">
              {/* Sort/Filter Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex cursor-pointer dark:bg-stone-900 items-center gap-2 px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-[var(--color-primary-500)] transition-all duration-200"
                >
                  <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                    />
                  </svg>
                  <span className="text-xs dark:text-gray-300">{t('common.filters')}</span>
                  <svg
                    className={`h-3 w-3 text-gray-500 transition-transform ${showSortDropdown ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showSortDropdown && (
                  <div className="absolute top-full mt-2 left-0 w-48 bg-white border-2 border-gray-200 rounded-2xl shadow-xl z-10">
                    <div className="py-2 text-xs">
                      {/* Type Filter */}
                      <div className="px-4 py-2 font-medium text-gray-800 border-b border-gray-100">{t('variants.filter_by_type')}</div>
                      <button
                        onClick={() => {
                          setFilterType("all");
                          setShowSortDropdown(false);
                        }}
                        className={`w-full cursor-pointer text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                          filterType === "all" ? "bg-[var(--color-primary-50)] text-[var(--color-primary-500)] font-medium" : "text-gray-700"
                        }`}
                      >
                        {t('variants.all_types')}
                      </button>
                      {variantTypes.map((type) => (
                        <button
                          key={type}
                          onClick={() => {
                            setFilterType(type);
                            setShowSortDropdown(false);
                          }}
                          className={`w-full cursor-pointer text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                            filterType === type ? "bg-[var(--color-primary-50)] text-[var(--color-primary-500)] font-medium" : "text-gray-700"
                          }`}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      ))}

                      {/* Sort Options */}
                      <div className="px-4 py-2 font-medium text-gray-800 border-t border-gray-100 mt-2">{t('common.sort_by')}</div>
                      {[
                        { value: "name", label: t('common.alphabetical') },
                        { value: "type", label: t('variants.sort_type') },
                        { value: "newest", label: t('common.newest_first') },
                        { value: "oldest", label: t('common.oldest_first') },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setShowSortDropdown(false);
                          }}
                          className={`w-full cursor-pointer text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                            sortBy === option.value ? "bg-[var(--color-primary-50)] text-[var(--color-primary-500)] font-medium" : "text-gray-700"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* View Toggle */}
              <div className="flex bg-gray-100 max-h-max rounded-xl ml-2 overflow-hidden">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-3 cursor-pointer max-h-max transition-all duration-200 ${
                    viewMode === "grid" ? "bg-[var(--color-primary-500)] text-white" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-3 cursor-pointer max-h-max transition-all duration-200 ${
                    viewMode === "list" ? "bg-[var(--color-primary-500)] text-white" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Loading and error feedback */}
        {loading &&
          <div className="flex justify-center text-[var(--color-primary-500)] text-5xl mt-40">
            <Loading />
          </div>
        }
        {error && <p className="text-center text-red-500">{error}</p>}

        {!loading && !error && (
          <>
            {/* Results Count */}
            <div className="mb-8">
              <span className="inline-flex items-center px-4 py-2 bg-[var(--color-primary-50)] text-[var(--color-primary-500)] rounded-full text-[10px] font-medium">
                {filteredVariants.length === 1 ? t('variants.found_count').replace('{count}', filteredVariants.length) : t('variants.found_count_plural').replace('{count}', filteredVariants.length)}
              </span>
            </div>

            {/* Variants Display */}
            {filteredVariants.length > 0 ? (
              viewMode === "grid" ? (
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 max-xl:gap-4">
                  {filteredVariants.map((variant) => (
                    <VariantCard key={variant.id} variant={variant} getTypeColor={getTypeColor} fetchVariants={fetchVariants} />
                  ))}
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredVariants.map((variant) => (
                    <VariantList key={variant.id} variant={variant} getTypeColor={getTypeColor} fetchVariants={fetchVariants} />
                  ))}
                </div>
              )
            ) : (
              /* Empty State */
              <div className="text-center py-10">
                <div className="w-28 h-28 mx-auto mb-8 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                  <svg className="h-12 w-12 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t('variants.no_variants_found')}</h3>
                <p className="text-gray-600 text-sm mb-6">
                  {t('variants.no_results_desc')}
                </p>
                {
                  role === 1 || (role === 2 && roles.add_variant === 1) ?
                    <Link to="/variants/create">
                      <button className="bg-[var(--color-primary-500)] cursor-pointer text-sm hover:bg-[var(--color-primary-400)] text-white font-semibold py-3 px-6 rounded-2xl transition-all duration-300">
                        {t('variants.create_first')}
                      </button>
                    </Link>
                  :''
                }
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
